import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardIcon, farmers, images } from "../../config/images";
import { Box, Button, Dialog, DialogContent, FormControl, MenuItem, Select, TextField, Typography, Table, TableHead, TableBody, TableCell, TableRow, Stack, InputAdornment, Radio} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import "./Farmer.css";
import Pagination from "@mui/material/Pagination";
import { styled } from "@mui/material/styles";
import translations from "../../config/language";
import { getFarmerListExcel, getFarmersList, getFarmingMethodOptions, getLocalGovernmentDetails, getRegionList, getStateDetails } from "../../config/services";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import { notifyError, notifySuccess } from "../../config/ToastMessage";
import { useSelector } from "react-redux";
import { downloadFile } from "../../config/DownloadFile";

const Farmers = () => {
  const navigate = useNavigate();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return parseInt(params.get('page')) || 1;
  });
  const [count, setCount] = useState();
  const [farmerData, setFarmerData] = useState([]);
  const [searchText, setSearchText] = useState();
  const [debounceSearchText, setDebouncedSearchText] = useState();
  const [regionData, setRegionData] = useState([]);
  const [stateData, setStateData] = useState([]);
  const [localGovernmentData, setLocalGovernmentData] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(""); // Store selected region
  const [selectedState, setSelectedState] = useState(""); // Store selected state
  const [selectedLocalGov, setSelectedLocalGov] = useState(""); // Store selected Local government
  const [timeFilterType, setTimeFilterType] = useState('within'); // 'within', 'moreThan', 'between'
  const [withinValue, setWithinValue] = useState('');
  const [moreThanValue, setMoreThanValue] = useState('');
  const [withinUnit, setWithinUnit] = useState('Weeks');
  const [moreThanUnit, setMoreThanUnit] = useState('Weeks');
  const [farmingMethodData, setFarmingMethodData] = useState([]);

  const [filterValues, setFilterValues] = useState({
    status: "",
    timePeriod: "",
    fromDate: null,
    toDate: null,
    location: "",
    farmingCategory: "",
    infrastructure: "",
    gender: "",
    region: "",
    state: "",
    localGov: "",
  });

  const { module_access } = useSelector((state) => state.userState)
  
  // Open Filter Popup
  const handleFilterClick = (event) => {
    setIsFilterOpen(true);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText); // Update the debounced searchText
    }, 500); // 500ms debounce delay

    return () => {
      clearTimeout(handler); // Cleanup the timeout
    };
  }, [searchText]); // Effect runs when searchText changes

  const fetchData = async () => {
    const region = await getRegionList();

    setRegionData(region.data.payload);

    const farmingMethod = await getFarmingMethodOptions();
    setFarmingMethodData(farmingMethod.data.results.payload);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getStateBasedOnRegion = async (regionId) => {
    try {
      const resp = await getStateDetails(regionId);
      if (resp.data.results.success == true) {
        setStateData(resp.data.results.payload);
      }
    } catch (error) {
      console.log("error", error);
      notifyError(error.response.data.message);
    }
  };
  useEffect(() => {
    if (selectedRegion) {
      getStateBasedOnRegion(selectedRegion);
    }
  }, [selectedRegion]);

  const getLocalGovBasedOnState = async (stateId, regionId) => {
    const data = {
      region_id: regionId,
      state_id: stateId,
    };
    try {
      const resp = await getLocalGovernmentDetails(data);
      if (resp.data.results.success == true) {
        setLocalGovernmentData(resp.data.results.payload);
      }
    } catch (error) {
      console.log("error", error);
      notifyError(error.response.data.message);
    }
  };
  useEffect(() => {
    if (selectedState && selectedRegion) {
      getLocalGovBasedOnState(selectedState, selectedRegion);
    }
  }, [selectedState, selectedRegion]);


  const handleFilterChange = (eventOrValue, fieldName) => {
    if (typeof eventOrValue === "object" && eventOrValue?.target) {
      // This is an event from a Select or regular input
      const { name, value } = eventOrValue.target;
      setFilterValues((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      // This is a direct value from DatePicker
      if (fieldName === 'fromDate' || fieldName === 'toDate') {
        // Handle null case when date is cleared
        if (!eventOrValue) {
          setFilterValues((prev) => ({
            ...prev,
            [fieldName]: null,
          }));
          return;
        }

        const selectedDate = dayjs(eventOrValue);
        const today = dayjs();

        if (selectedDate.isAfter(today)) {
          if (fieldName === 'fromDate') {
            notifyError(translations[language].fromDateCantBeGreaterThanToday);
          } else {
            notifyError(translations[language].toDateCantBeGreaterThanToday);
          }
          return;
        }

        // Validate fromDate is not after toDate
        if (fieldName === 'fromDate' && filterValues.toDate) {
          if (selectedDate.isAfter(filterValues.toDate)) {
            notifyError(translations[language].fromDateCantBeAfterToDate);
            return;
          }
        }

        // Validate toDate is not before fromDate
        if (fieldName === 'toDate' && filterValues.fromDate) {
          if (selectedDate.isBefore(filterValues.fromDate)) {
            notifyError(translations[language].toDateCantBeBeforeFromDate);
            return;
          }
        }
      }

      setFilterValues((prev) => ({
        ...prev,
        [fieldName]: eventOrValue,
      }));
    }
  };

  const handleVisibilityClick = (id) => {
    navigate(`/farmer/farmer-detail/${id}?page=${currentPage}`);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const page = parseInt(params.get('page')) || 1;
    setCurrentPage(page);
  }, [window.location.search]);

  const handleClear = () => {
    setFilterValues({
      status: "",
      timePeriod: "",
      fromDate: null,
      toDate: null,
      location: "",
      farmingCategory: "",
      infrastructure: "",
      gender: "",
      region: "",
    });
    setWithinValue('');
    setMoreThanValue('');
    setWithinUnit('Weeks');
    setMoreThanUnit('Weeks');
    setTimeFilterType('within');
    setSelectedRegion('');
    setSelectedState('');
    setSelectedLocalGov('');
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    const url = new URL(window.location.href);
    url.searchParams.set('page', value);
    window.history.pushState({}, '', url);
  };

  const [isFilteredData, setIsFilterData] = useState(false);
  const handleFilterSubmit = () => {
    setIsFilterData(true);
    setIsFilterOpen(false); // Close filter popup
  };
  useEffect(() => {
    if (isFilteredData) {
      fetchFarmerData();
    }
  }, [isFilteredData]);
  const fetchFarmerData = async () => {

    const data = isFilteredData
      ? {
        status: filterValues.status,
        gender: filterValues.gender,
        start_date: filterValues.fromDate ? dayjs(filterValues.fromDate).format("YYYY-MM-DD") : null,
        end_date: filterValues.toDate ? dayjs(filterValues.toDate).format("YYYY-MM-DD") : null,
        count: timeFilterType === 'within' ? withinValue : moreThanValue,
        ...(withinValue && timeFilterType === 'within' ? {
          count: withinValue,
          within_the_last: withinUnit
        } : {}),
        ...(moreThanValue && timeFilterType === 'moreThan' ? {
          count: moreThanValue,
          more_than: moreThanUnit
        } : {}),
        region_name: filterValues.region,
        state_name: filterValues.state,
        local_government_name: filterValues.localGov,
        search: debounceSearchText,
        farming_method_name: filterValues.farmingCategory,
      }
      : { search: debounceSearchText }; // If not filtered, send an empty object
    try {
      const resp = await getFarmersList(data, currentPage, perPage);
      if (resp.data.results.success == true) {
        setFarmerData(resp.data.results.payload);
        setCount(resp.data.count);
        setIsFilterData(false);
      }
    } catch (error) {
      console.log(error, "error");
      notifyError(error.response.data.message);
    }
  }

  useEffect(() => {
    fetchFarmerData();
  }, [currentPage, debounceSearchText]);

  const CustomPagination = styled(Pagination)(({ theme }) => ({
    "& .MuiPaginationItem-root": {
      borderColor: "none",
      borderWidth: "none",
      borderStyle: "none",
      marginTop: "30px",
    },
    "& .Mui-selected": {
      borderColor: "#31A05F",
      backgroundColor: "#31A05F !important",
      color: "#ffffff !important",
    },
  }));

  const language = process.env.REACT_APP_LANGUAGE;

  const handleExcelDownload = async () => {
    try {
      const res = await getFarmerListExcel();
      if (res.data.success == true) {
        downloadFile(res);
        notifySuccess(translations[language].downloadSuccess);
      }
    } catch (error) {
      console.log(error, "error");
      notifyError(error.response.data.message);
    }
  }
  return (
    <>
      {/* <Grid container spacing={2}>
          <Grid item xs={12}> */}
      <Box className="task-assign-box">
        <Box
          className="d-flex justify-content-between align-items-center"
          style={{ flexWrap: "wrap" }}
        >
          <Box
            className="d-flex gap-3 img-box"
            style={{ padding: "25px" }}
          >
            <img src={farmers.farmerIcon} alt="" />
            <Typography variant="h6" gutterBottom>
              {translations[language].farmersList}
            </Typography>
          </Box>
          <div className="edit-profile">
            <img onClick={() => handleExcelDownload()} src={farmers.excelSheetIcon} alt="" />
            <img src={farmers.pdfIcon} alt="" />
            <div>
              <img
                src={farmers.filterIcon}
                alt=""
                onClick={handleFilterClick}
              />
            </div>
          </div>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid #31A05F",
            borderRadius: "8px",
            overflow: "hidden",
            margin: "0px 20px",
            boxShadow: "0px 4px 10px rgba(151, 151, 151, 0.12)",
          }}
          className="mt-3 mb-4"
        >
          <TextField
            placeholder="Search here"
            variant="outlined"
            size="small"
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
                paddingRight: "0px",
                "& fieldset": {
                  border: "none",
                },
              },
            }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{
                    color: "#FFFFFF",
                    fontWeight: "400",
                    backgroundColor: "#31A05F",
                    height: "100%",
                    padding: "0px",
                    maxHeight: "unset",
                    padding: "0px 15px",
                  }}
                >
                  <SearchIcon sx={{ color: "#FFFFFF", mr: 1 }} />
                  Search
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box className="crop-table-container">
          <Table className="region-table pt-1" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell style={{ textAlign: "center" }}>{translations[language].srNo}</TableCell>
                <TableCell>{translations[language].photo}</TableCell>
                <TableCell>{translations[language].farmerName}</TableCell>
                <TableCell>{translations[language].date}</TableCell>
                <TableCell>{translations[language].dataCollectorName}</TableCell>
                <TableCell>{translations[language].mobileNo}</TableCell>
                <TableCell>{translations[language].location}</TableCell>
                <TableCell>{translations[language].statusView}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {farmerData.length > 0 ? farmerData.map((farmer, index) => {
                return (
                  <TableRow key={farmer.id}>
                    <TableCell style={{ textAlign: "center" }}>{index + 1 + (currentPage - 1) * perPage}</TableCell>
                    <TableCell>
                      {farmer.farmer_photo ? <img
                        src={farmer.farmer_photo}
                        alt=""
                        className="farmer-photo"
                      /> : <img
                        src={dashboardIcon.profileImage}
                        alt=""
                        className="farmer-photo"
                      />}

                    </TableCell>
                    <TableCell>{farmer.first_name} {farmer.middle_name} {farmer.last_name}</TableCell>
                    <TableCell>{dayjs(farmer.created_at).format("YYYY/MM/DD")}</TableCell>
                    <TableCell>{farmer.created_by_name}</TableCell>
                    <TableCell>{farmer.mobile_number}</TableCell>
                    <TableCell>{farmer.farmer_house_address}</TableCell>
                    <TableCell>
                      <div className="d-flex gap-4">
                        <div style={{ width: "50px", color: farmer.status == "10" ? "#31A05F" : "" }}
                        >
                          {farmer.status == 9 ?
                            translations[language].pending
                            : farmer.status == 10 ?
                              translations[language].approved
                              : translations[language].reject}
                        </div>
                        <VisibilityIcon
                          onClick={() => handleVisibilityClick(farmer.id)}
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                )
              }) : <TableRow>
                <TableCell colSpan={8} style={{ textAlign: "center" }}>
                  <img src={farmers.noData} alt="No Data" />
                </TableCell>
              </TableRow>}
            </TableBody>
          </Table>
        </Box>
        <Box display="flex" justifyContent="end" alignItems="center" className="pagination">
          <Stack spacing={2} className="mb-3">
            <CustomPagination
              count={Math.ceil(count / perPage)}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
            />
          </Stack>
        </Box>
        {/* Filter Popup */}
        <Dialog
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          className="filter-popup farmer-filter-popup"
          maxWidth="xs"
          fullWidth
          disableScrollLock
          PaperProps={{
            sx: { borderRadius: "12px" },
          }}
        >
          <DialogContent style={{ padding: "10px" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0,
                mt: 0,
              }}
            >
              <Typography variant="h6">{translations[language].filter}</Typography>
              <Button onClick={handleClear} sx={{ color: "#666" }}>
                {translations[language].clear}
              </Button>
            </Box>

            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <FormControl fullWidth size="small">
                <Select
                  value={filterValues.status}
                  onChange={handleFilterChange}
                  displayEmpty
                  IconComponent={ArrowDropDownIcon}
                  className="filter-select"
                  name="status"
                >
                  <MenuItem value="" hidden>
                    {translations[language].selectStatus}
                  </MenuItem>
                  <MenuItem value="9">{translations[language].pending}</MenuItem>
                  <MenuItem value="10">{translations[language].approved}</MenuItem>
                  <MenuItem value="11">{translations[language].reject}</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Within Option */}
                <FormControl>
                  <div style={{ display: 'flex', alignItems: 'center', minHeight: '40px' }}>
                    <Radio
                      checked={timeFilterType === 'within'}
                      onChange={(e) => {
                        setTimeFilterType('within');
                        setMoreThanValue(''); // Clear moreThan value
                        setMoreThanUnit('Weeks'); // Reset moreThan unit
                        setFilterValues(prev => ({
                          ...prev,
                          fromDate: null,
                          toDate: null
                        }));
                      }}
                      value="within"
                      name="time-filter"
                      style={{ paddingLeft: "0px" }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                      <span style={{ minWidth: '85px' }}>Within last</span>
                      <TextField
                        size="small"
                        type="number"
                        value={withinValue}
                        onChange={(e) => {
                          const value = Math.max(0, Number(e.target.value));
                          setWithinValue(value);
                        }}
                        disabled={timeFilterType !== 'within'}
                        style={{ width: '25%' }}
                      />
                      <Select
                        value={withinUnit}
                        onChange={(e) => setWithinUnit(e.target.value)}
                        size="small"
                        disabled={timeFilterType !== 'within'}
                        style={{ width: '50%' }}
                      >
                        <MenuItem value="weeks">Weeks</MenuItem>
                        <MenuItem value="hours">Hours</MenuItem>
                        <MenuItem value="days">Days</MenuItem>
                      </Select>
                    </div>
                  </div>
                </FormControl>

                {/* More Than Option */}
                <FormControl>
                  <div style={{ display: 'flex', alignItems: 'center', minHeight: '40px' }}>
                    <Radio
                      checked={timeFilterType === 'moreThan'}
                      onChange={(e) => {
                        setTimeFilterType('moreThan');
                        setWithinValue(''); // Clear within value
                        setWithinUnit('Weeks'); // Reset within unit
                        setFilterValues(prev => ({
                          ...prev,
                          fromDate: null,
                          toDate: null
                        }));
                      }}
                      value="moreThan"
                      name="time-filter"
                      style={{ paddingLeft: "0px" }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                      <span style={{ minWidth: '85px' }}>More Than</span>
                      <TextField
                        size="small"
                        type="number"
                        value={moreThanValue}
                        onChange={(e) => {
                          const value = Math.max(0, Number(e.target.value));
                          setMoreThanValue(value);
                        }}
                        disabled={timeFilterType !== 'moreThan'}
                        style={{ width: '25%' }}
                      />
                      <Select
                        value={moreThanUnit}
                        onChange={(e) => setMoreThanUnit(e.target.value)}
                        size="small"
                        disabled={timeFilterType !== 'moreThan'}
                        style={{ width: '50%' }}
                      >
                        <MenuItem value="weeks">Weeks</MenuItem>
                        <MenuItem value="hours">Hours</MenuItem>
                        <MenuItem value="days">Days</MenuItem>
                      </Select>
                    </div>
                  </div>
                </FormControl>

                {/* Between Option */}
                <FormControl>
                  <div style={{ display: 'flex', alignItems: 'center', minHeight: '40px' }}>
                    <Radio
                      checked={timeFilterType === 'between'}
                      onChange={(e) => setTimeFilterType('between')}
                      value="between"
                      name="time-filter"
                      style={{ paddingLeft: "0px" }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                      <span style={{ minWidth: '85px' }}>Between</span>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label={translations[language].fromDate}
                          value={filterValues.fromDate}
                          name="fromDate"
                          disabled={timeFilterType !== 'between'}
                          onChange={(value) => handleFilterChange(value, "fromDate")}
                          disableFuture
                          maxDate={filterValues.toDate}
                        />
                      </LocalizationProvider>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label={translations[language].toDate}
                          value={filterValues.toDate}
                          disabled={timeFilterType !== 'between'}
                          name="toDate"
                          onChange={(value) => handleFilterChange(value, "toDate")}
                          disableFuture
                          minDate={filterValues.fromDate}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                </FormControl>
              </Box>
              <FormControl fullWidth size="small">
                <Select
                  value={filterValues.farmingCategory}
                  onChange={handleFilterChange}
                  displayEmpty
                  IconComponent={ArrowDropDownIcon}
                  className="filter-select"
                  name="farmingCategory"
                >
                  <MenuItem value="" hidden>
                    {translations[language].selectFarmingCategory}
                  </MenuItem>
                  {farmingMethodData?.map((category, index) => (
                    <MenuItem key={index} value={category.name}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <Select
                  value={filterValues.gender}
                  onChange={handleFilterChange}
                  displayEmpty
                  IconComponent={ArrowDropDownIcon}
                  className="filter-select"
                  name="gender"
                >
                  <MenuItem value="" hidden>
                    {translations[language].selectGender}
                  </MenuItem>
                  <MenuItem value="0">{translations[language].male}</MenuItem>
                  <MenuItem value="1">{translations[language].female}</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <Select
                  name="region"
                  displayEmpty
                  value={selectedRegion}
                  onChange={(e) => {
                    const selectedOption = regionData.find(item => item.id === e.target.value);
                    setSelectedRegion(selectedOption.id);
                    setFilterValues(prev => ({
                      ...prev,
                      region: selectedOption.region_name
                    }));
                  }}
                >
                  <MenuItem value="" hidden>
                    {translations[language].selectRegion}
                  </MenuItem>
                  {regionData.map((item, index) => (
                    <MenuItem value={item.id}>{item.region_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <Select
                  name="state"
                  value={selectedState}
                  displayEmpty
                  onChange={(e) => {
                    const selectedOption = stateData.find(item => item.id === e.target.value);
                    setSelectedState(selectedOption.id);
                    setFilterValues(prev => ({
                      ...prev,
                      state: selectedOption.state_name
                    }));
                  }}
                >
                  <MenuItem value="" hidden>
                    {translations[language].selectState}
                  </MenuItem>
                  {selectedRegion && stateData.length === 0 ? (
                    <MenuItem disabled>
                      {translations[language].noOptionAvailable}
                    </MenuItem>
                  ) : (
                    !selectedRegion && (
                      <MenuItem disabled>
                        {translations[language].pleaseSelectRegion}
                      </MenuItem>
                    )
                  )}

                  {stateData.map((item, index) => (
                    <MenuItem value={item.id}>{item.state_name}</MenuItem>
                  ))}
                </Select>
                </FormControl>
              <FormControl fullWidth>
                <Select
                  name="localGovernment"
                  displayEmpty
                  value={selectedLocalGov}
                  onChange={(e) => {
                    const selectedOption = localGovernmentData.find(item => item.id === e.target.value);
                    setSelectedLocalGov(selectedOption.id);
                    setFilterValues(prev => ({
                      ...prev,
                      localGov: selectedOption.local_government_name
                    }));
                  }}
                >
                  <MenuItem value="" hidden>
                    {translations[language].selectLocalGovernment}
                  </MenuItem>
                  {selectedRegion &&
                    selectedState &&
                    localGovernmentData.length === 0 ? (
                    <MenuItem disabled>
                      {translations[language].noOptionAvailable}
                    </MenuItem>
                  ) : (
                    (!selectedRegion || !selectedState) && (
                      <MenuItem disabled>
                        {translations[language].pleaseSelectState}
                      </MenuItem>
                    )
                  )}
                  {localGovernmentData.map((item, index) => (
                    <MenuItem value={item.id}>
                      {item.local_government_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                fullWidth
                variant="contained"
                onClick={handleFilterSubmit}
                sx={{
                  backgroundColor: "#31A05F",
                  "&:hover": {
                    backgroundColor: "#2b8f54",
                  },
                }}
              >
                {translations[language].submit}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      </Box >
      {/* </Grid>
        </Grid> */}
    </>
  );
};

export default Farmers;
